import { AddAPhoto, HideImage } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import { NeedsClusterReinstalledImporterDoorLabelPhotoDto, NeedsClusterReinstalledImporterDoorLabelPhotoDtoSchema } from 'dtos'
import { Formik } from 'formik'
import { useLifecycleDocumentPreview } from '../checkIn/checkInPhotoDialogCommon'

interface ImporterDoorLabelPhotoDialogProps {
  initialValues: NeedsClusterReinstalledImporterDoorLabelPhotoDto
  isLoading: boolean
  onClose: () => void
  onDelete: () => void
  onSave: (values: NeedsClusterReinstalledImporterDoorLabelPhotoDto) => void
  open: boolean
  vehicleGuid: string // Used to preview image
}

export default function ImporterDoorLabelPhotoDialog({
  initialValues,
  isLoading,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: ImporterDoorLabelPhotoDialogProps) {
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getNeedsClusterReinstalledPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={NeedsClusterReinstalledImporterDoorLabelPhotoDtoSchema}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Importer Photo Details</DialogTitle>

                  <Grid item xs={12}>
                    <img
                      alt='Importer Door Label'
                      src={
                        values.clusterReinstallImporterDoorLabelPhotoFile
                          ? URL.createObjectURL(values.clusterReinstallImporterDoorLabelPhotoFile)
                          : serverSrc
                      }
                      style={{ width: '100%' }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {errors.clusterReinstallImporterDoorLabelPhotoFile && (
                      <Typography color='error' variant='body2'>
                        {errors.clusterReinstallImporterDoorLabelPhotoFile}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <input
                      accept='image/png, image/jpeg'
                      hidden
                      id='fileInput'
                      type='file'
                      onChange={e => {
                        if (e.target.files && e.target.files.length > 0) {
                          setFieldValue('clusterReinstallImporterDoorLabelPhotoFile', e.target.files[0])
                        }
                      }}
                    />

                    <Button
                      color='primary'
                      disabled={isSubmitting || isLoading}
                      endIcon={<AddAPhoto />}
                      fullWidth
                      onClick={() => {
                        document.getElementById('fileInput')?.click()
                      }}
                      variant='outlined'
                    >
                      BROWSE OR TAKE PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading || !Boolean(values.documentGuid)}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Importer Door Label?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Photo Previewer:**

Displays a preview of the uploaded photo.

If no photo is uploaded, nothing will be displayed.

**BROWSE OR TAKE PHOTO Button:**

Clicking this button opens the user’s camera app, allowing
them to:

Take a new photo and upload it.

Upload an existing photo from their device.

**DELETE UPLOADED PHOTO Button:**

Clicking this button will:

Clear/delete the currently uploaded photo.

Remove it from the Photo Previewer.

**CANCEL Button:**

Clicking the CANCEL button will close the dialog and not
save any changes

**SAVE Button:**

Clicking the NEXT button will:

Save the uploaded photo to the database and close the
dialog

`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='error' disabled={isSubmitting || isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <Button color='primary' disabled={isSubmitting || isLoading} type='submit' variant='contained'>
                  SAVE
                </Button>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
